import React, { useState, useEffect } from "react";
import DayWeekTab, {
  ViewMode,
} from "Pages/MyScheduleBeta/Components/FiltersSchedule/DayWeekTab";
import Title from "Pages/MyScheduleBeta/Components/FiltersSchedule/Title";
import MyScheduleBetaFilter from "Pages/MyScheduleBeta/Components/MyScheduleBetaFilter";
import MyScheduleCalendarHeader from "Pages/MyScheduleBeta/Components/MyScheduleCalendarHeader";
import { DAY_WEEK_TYPE } from "Pages/MyScheduleBeta/utils/constants";
import {
  INITIAL_WEEK_END_DATE,
  INITIAL_WEEK_START_DATE,
  useScheduleBetaContext,
} from "Pages/MyScheduleBeta/Context/ScheduleBetaContext";
import useWindowSize from "utilFunctions/CustomHooks/useWindowSize";
import { StyledDivHeader } from "Pages/DRS/DRSHeader";
import Unmarked from "Pages/MyScheduleBeta/Components/FiltersSchedule/Unmarked";
import AutoSearchInput from "utilFunctions/shared/AutoSearchInput"; // Import AutoSearchInput
import { BASE_URL, GLOBAL_SEARCH } from "utilFunctions/Constants/urlConstants";
import { useGetApi } from "utilFunctions/CustomHooks/useGetAPI";
import { useMyScheduleBeta } from "Pages/MyScheduleBeta/Hooks/useMyScheduleBeta";
import { toISTStart, toISTEnd } from "Pages/MyScheduleBeta/utils/dateHelpers";
import { useNavigate } from "react-router-dom";

// ── Component ─────────────────────────────────────────────────────────────────

const MyScheduleBetaHeader = () => {
  const {
    eventType,
    setEventType,
    isNotmarkedVisible,
    setIsNotmarkedVisible,
    filterState,
    setFilterState,
    selectedDate,
  } = useScheduleBetaContext();
  const { screen } = useWindowSize();
  const navigate = useNavigate();
  const { handleSearchFaculty, CallMyScheduleApi } = useMyScheduleBeta();

  const handleBack = () => {
    if (isNotmarkedVisible) {
      setIsNotmarkedVisible(false);
      CallMyScheduleApi();
      return;
    }

    navigate("/admin/dashboard");
  };

  // State required by AutoSearchInput
  const [input, setInput] = useState<string>("");
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [isLoader, setIsLoader] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [offset, setOffset] = useState<number>(0);
  const [searchURL, setSearchURL] = useState<string>("");
  const [searchName, setSearchName] = useState<any>(null);
  const [selectedFaculty, setSelectedFaculty] = useState<any>(null);
  const limit = 10;

  useEffect(() => {
    if (localStorage.getItem("scheduledFilter")) {
      const parsedFilter = JSON.parse(
        localStorage.getItem("scheduledFilter") as any
      );
      if (parsedFilter) {
        setSearchName(parsedFilter.email);
        setInput(parsedFilter.email);
        setSelectedFaculty(parsedFilter);
        setFilterState((prev: any) => ({
          ...prev,
          facultySearch: parsedFilter,
        }));
        if (handleSearchFaculty) {
          handleSearchFaculty(parsedFilter);
        }
      }
    }
  }, [localStorage.getItem("scheduledFilter")]);

  // API Call setup
  const { makeRequest: GetSearchData, responseData: searchData } = useGetApi(
    () => setIsLoader(false),
    (error: any) => {
      console.error("Search API Error:", error);
      setIsLoader(false);
      setHasMore(false);
    }
  );

  // Sync API Response to Suggestions
  useEffect(() => {
    if (searchData && searchData["data"]) {
      if (offset === 0) {
        setSuggestions([...searchData["data"]]);
      } else {
        setSuggestions((prev) => [...prev, ...searchData["data"]]);
      }
      setHasMore(searchData["data"].length > 0);
    }
  }, [searchData, offset]);

  useEffect(() => {
    if (filterState?.clearFacultySearchInput) {
      setInput("");
      setSearchName(null);
      setSuggestions([]);
      setOffset(0);
      setSearchURL("");
      setHasMore(false);
      setSelectedFaculty(null);

      // Reset the flag so it doesn't keep clearing the input
      setFilterState((prev: any) => ({
        ...prev,
        clearFacultySearchInput: false,
      }));
    }
  }, [filterState?.clearFacultySearchInput]);

  // Handler triggered by AutoSearchInput when user types
  const searchDataHandler = (searchTerm: string) => {
    const url = `${
      BASE_URL + GLOBAL_SEARCH
    }?name=${searchTerm}&moduleType=faculty&fexRole=Faculty,Academic Head,Regional Head`;
    setSearchURL(url);
    setOffset(0);
    GetSearchData(`${url}&limit=${limit}&offset=0`);
  };

  // Pagination Next Handler
  const nextHandler = () => {
    const newOffset = offset + limit;
    setOffset(newOffset);
    GetSearchData(`${searchURL}&limit=${limit}&offset=${newOffset}`);
  };

  // Selection Handler
  const onClickHandler = (selectedFaculty: any) => {
    localStorage.setItem("scheduledFilter", JSON.stringify(selectedFaculty));
    setSelectedFaculty(selectedFaculty);
    setFilterState((prev: any) => ({
      ...prev,
      facultySearch: selectedFaculty,
      clearFacultySearchInput: false,
    }));
    if (handleSearchFaculty) {
      handleSearchFaculty(selectedFaculty);
    }
  };

  const handleResetSearchfaculty = () => {
    localStorage.removeItem("scheduledFilter");
    if (handleSearchFaculty) {
      handleSearchFaculty(null);
    }
    setSelectedFaculty(null);
    setInput("");
    setSearchName(null);
    setSuggestions([]);
    setFilterState((prev: any) => ({
      ...prev,
      facultySearch: "",
      clearFacultySearchInput: false,
    }));
  };

  const onDayWeekChangeHandler = (value: ViewMode) => {
    console.log("valuevalue", value, filterState);
    setEventType(value);
    const selDate = selectedDate ? new Date(selectedDate) : new Date();
    if (value === DAY_WEEK_TYPE.WEEK) {
      // const start = subDays(selDate, 3);
      // const end = addDays(selDate, 3);
      setFilterState((prev) => ({
        ...prev,
        startDate: INITIAL_WEEK_START_DATE,
        endDate: INITIAL_WEEK_END_DATE,
      }));
    } else {
      setFilterState((prev) => ({
        ...prev,
        startDate: toISTStart(selDate),
        endDate: toISTEnd(selDate),
      }));
    }
  };

  const isDesktop = ["xl", "xxl", "lg"].includes(screen);

  return (
    <StyledDivHeader
      className="d-flex flex-column justify-content-between p-0"
      style={{
        background: "white",
        width: "100%",
        boxSizing: "border-box",
      }}
      isQuestionSessionStarted={false}
    >
      <div className="d-flex justify-content-between align-items-center gap-3">
        {isDesktop && <Title onBack={handleBack} />}
        <div
          className={
            isDesktop
              ? "d-flex justify-content-end align-items-center gap-4 ml-auto"
              : "d-flex justify-content-between w-100 pl-2 align-items-center"
          }
        >
          {/* Direct AutoSearchInput integration */}
          {isDesktop && (
            <div
              className="d-flex flex-row p-0 align-items-center"
              style={
                ["md", "lg"].includes(screen)
                  ? { minWidth: "160px", maxWidth: "200px", flexShrink: 1 }
                  : { minWidth: "300px" }
              }
            >
              <div style={{ width: "100%", minWidth: 0 }}>
                <AutoSearchInput
                  input={input}
                  setInput={setInput}
                  suggestions={suggestions}
                  setSuggestions={setSuggestions}
                  isLoader={isLoader}
                  setIsLoader={setIsLoader}
                  searchData={searchDataHandler}
                  searchName={searchName}
                  setSearchName={setSearchName}
                  placeholder="Search by faculty email"
                  nextHandler={nextHandler}
                  hasMore={hasMore}
                  mode="faculty_search"
                  onClickHandler={onClickHandler}
                />
              </div>
              {selectedFaculty != null ? (
                <div
                  onClick={() => {
                    handleResetSearchfaculty();
                  }}
                  className="d-flex justify-content-center align-items-center cursor-pointer pl-2"
                  style={{ flexShrink: 0 }}
                >
                  <span className="filterspanWidth">Reset </span>
                </div>
              ) : null}
            </div>
          )}

          <div
            style={
              ["md", "lg"].includes(screen) ? { flexShrink: 0 } : undefined
            }
          >
            <Unmarked></Unmarked>
          </div>

          <div
            className="d-flex align-items-center"
            style={
              ["md", "lg"].includes(screen)
                ? { gap: "6px", flexShrink: 0 }
                : { gap: "16px" }
            }
          >
            {!isNotmarkedVisible && (
              <DayWeekTab
                onChange={onDayWeekChangeHandler}
                value={eventType}
              ></DayWeekTab>
            )}
            {!isNotmarkedVisible && <MyScheduleBetaFilter />}
          </div>
        </div>
      </div>
      {!isNotmarkedVisible && (
        <MyScheduleCalendarHeader></MyScheduleCalendarHeader>
      )}
    </StyledDivHeader>
  );
};

export default MyScheduleBetaHeader;
